import { CheckCircle, Close, ExpandLess, ErrorOutline } from '@mui/icons-material';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOcrQueue } from '@practium/features/analyses';
import { FloraAIIcon, useToast } from '@practium/ui';
import styles from './OcrQueueWidget.module.css';

export const OcrQueueWidget = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const { items, remove, isOpen, setIsOpen } = useOcrQueue();
	const knownItemIds = useRef(new Set(items.map((item) => item.id)));
	const notifiedErrorIds = useRef(
		new Set(items.filter((item) => item.status === 'error').map((item) => item.id))
	);
	const notifiedDoneIds = useRef(
		new Set(items.filter((item) => item.status === 'done').map((item) => item.id))
	);
	const finished = items.filter((item) => item.status === 'done').length;
	const isProcessing = items.some((item) =>
		['uploading', 'queued', 'processing'].includes(item.status)
	);
	const hasError = items.some((item) => item.status === 'error');
	const floraStatus = isProcessing
		? `A Flora está analisando ${items.length === 1 ? 'seu PDF' : 'seus PDFs'}`
		: hasError
			? 'A Flora encontrou um problema'
			: 'A Flora concluiu as análises';

	useEffect(() => {
		const addedItems = items.filter((item) => !knownItemIds.current.has(item.id));
		if (addedItems.length === 0) return;

		addedItems.forEach((item) => knownItemIds.current.add(item.id));
		toast.info(
			addedItems.length === 1
				? `PDF ${addedItems[0].fileName} carregado. A Flora iniciou a análise.`
				: `${addedItems.length} PDFs carregados. A Flora iniciou as análises.`
		);
	}, [items, toast]);

	useEffect(() => {
		const completedItems = items.filter(
			(item) => item.status === 'done' && !notifiedDoneIds.current.has(item.id)
		);

		completedItems.forEach((item) => {
			notifiedDoneIds.current.add(item.id);
			toast.dismiss('info');
			toast.success(`PDF ${item.fileName} carregado e analisado com sucesso!`);
		});
	}, [items, toast]);

	useEffect(() => {
		const newErrors = items.filter(
			(item) => item.status === 'error' && !notifiedErrorIds.current.has(item.id)
		);

		newErrors.forEach((item) => {
			notifiedErrorIds.current.add(item.id);
			toast.error(
				`Não foi possível analisar ${item.fileName}: ${item.error || 'o processamento do PDF falhou.'}`
			);
		});
	}, [items, toast]);

	if (items.length === 0) return null;

	const openItem = (item: (typeof items)[number]) => {
		if (item.status !== 'done' || !item.samples?.length) return;
		toast.dismiss('info');
		toast.info(`Abrindo PDF ${item.fileName}`);
		navigate(`/analises/novo?type=${item.analysisType === 'soil' ? 'soil' : 'plant'}`, {
			state: { ocrItemId: item.id },
		});
	};

	if (!isOpen) {
		return (
			<button
				className={styles.collapsed}
				onClick={() => setIsOpen(true)}
				aria-label="Abrir fila de OCR"
			>
				<FloraAIIcon
					className={`${styles.collapsedFlora} ${isProcessing ? styles.floraWorking : ''}`}
					aria-hidden="true"
				/>
				<span className={styles.progressCount}>
					{finished} de {items.length}
				</span>
				<ExpandLess />
			</button>
		);
	}

	return (
		<section className={styles.panel} aria-label="Processamento de laudos">
			<header className={styles.header}>
				<div className={styles.floraIdentity}>
					<div className={styles.floraAvatar} aria-hidden="true">
						<FloraAIIcon
							className={`${styles.floraIcon} ${isProcessing ? styles.floraWorking : ''}`}
						/>
					</div>
					<div className={styles.headerText}>
						<span className={styles.floraStatus} role="status" aria-live="polite">
							{floraStatus}
						</span>
						<div
							className={styles.progressSummary}
							role="status"
							aria-live="polite"
							aria-label={`${finished} de ${items.length} carregados`}
						>
							{isProcessing && <span className={styles.progressLoading} aria-hidden="true" />}
							<strong className={styles.progressCount} aria-hidden="true">
								{finished} de {items.length}
							</strong>
							<span className={styles.progressLabel} aria-hidden="true">
								carregados
							</span>
						</div>
					</div>
				</div>
				<button
					className={styles.iconButton}
					onClick={() => setIsOpen(false)}
					aria-label="Minimizar fila"
				>
					<Close />
				</button>
			</header>
			<div className={styles.list}>
				{items.map((item) => (
					<div
						key={item.id}
						className={`${styles.item} ${item.status === 'done' ? styles.ready : ''} ${item.status === 'error' ? styles.errorItem : ''}`}
						onClick={() => openItem(item)}
						role={item.status === 'done' ? 'button' : undefined}
						tabIndex={item.status === 'done' ? 0 : undefined}
						onKeyDown={(event) => {
							if (item.status === 'done' && (event.key === 'Enter' || event.key === ' '))
								openItem(item);
						}}
					>
						{item.status === 'done' && <CheckCircle className={styles.success} />}
						{item.status === 'error' && <ErrorOutline className={styles.error} />}
						{(item.status === 'uploading' ||
							item.status === 'queued' ||
							item.status === 'processing') && (
							<span className={styles.loading} role="status" aria-label="Carregando" />
						)}
						<span className={styles.fileName} title={item.fileName}>
							{item.fileName}
						</span>
						{item.status === 'error' && (
							<>
								<button
									className={styles.removeButton}
									onClick={(event) => {
										event.stopPropagation();
										remove(item.id);
										toast.success(`PDF ${item.fileName} foi removido da análise.`);
									}}
									aria-label={`Excluir ${item.fileName}`}
								>
									<Close />
								</button>
							</>
						)}
						{item.status === 'done' && (
							<>
								<span className={styles.openText}>Abrir</span>
								<button
									className={styles.removeButton}
									onClick={(event) => {
										event.stopPropagation();
										remove(item.id);
										toast.success(`PDF ${item.fileName} foi removido da análise.`);
									}}
									aria-label={`Excluir ${item.fileName}`}
								>
									<Close />
								</button>
							</>
						)}
					</div>
				))}
			</div>
		</section>
	);
};
